/*
 * BlackMirror Demo Attribution v1
 *
 * Captures first/last marketing touch on a Speediance Shopify storefront and
 * carries it into Cal.com using Cal.com's native UTM Tracking record. The full
 * payload is compacted into utm_content as `bm1.<base64url-json>`; it contains
 * marketing identifiers only and deliberately excludes names, email, phone,
 * arbitrary query parameters, and URL fragments.
 */
(function universalModule(root, factory) {
  "use strict";
  var api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  if (root) root.BMDemoAttribution = api;
  if (root && root.document) api.install(root);
})(typeof window !== "undefined" ? window : null, function factory() {
  "use strict";

  var VERSION = 1;
  var COOKIE_NAME = "bm_demo_attribution_v1";
  var STORAGE_KEY = "bm_demo_attribution_v1";
  var COOKIE_MAX_AGE = 90 * 24 * 60 * 60;
  var CAL_HOST = "cal.futuremvmt.com";
  var PACKED_PREFIX = "bm1.";
  var CLICK_KEYS = ["gclid", "gbraid", "wbraid"];
  var UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
  var EXTRA_KEYS = ["tmsrc", "tmcid", "tmsid", "tmid"];
  var ALL_MARKETING_KEYS = CLICK_KEYS.concat(UTM_KEYS, EXTRA_KEYS);

  function clean(value, maxLength) {
    var text = value == null ? "" : String(value).trim();
    if (!text) return "";
    return text.slice(0, maxLength || 256);
  }

  function canonicalUrl(value, base) {
    try {
      var parsed = new URL(value, base || undefined);
      if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return "";
      return clean(parsed.origin + parsed.pathname, 700);
    } catch (_error) {
      return "";
    }
  }

  function marketingParams(search) {
    var params;
    try {
      params = search instanceof URLSearchParams ? search : new URLSearchParams(search || "");
    } catch (_error) {
      params = new URLSearchParams();
    }
    var output = {};
    ALL_MARKETING_KEYS.forEach(function (key) {
      var value = clean(params.get(key), key.indexOf("utm_") === 0 ? 300 : 256);
      if (value) output[key] = value;
    });
    return output;
  }

  function hasMarketingSignal(params) {
    return ALL_MARKETING_KEYS.some(function (key) { return Boolean(params && params[key]); });
  }

  function isExternalReferrer(referrer, currentOrigin) {
    if (!referrer) return false;
    try {
      return new URL(referrer).origin !== currentOrigin;
    } catch (_error) {
      return false;
    }
  }

  function buildTouch(locationLike, referrer, capturedAt) {
    var origin = clean(locationLike && locationLike.origin, 300);
    var params = marketingParams(locationLike && locationLike.search);
    var touch = {
      captured_at: capturedAt || new Date().toISOString(),
      landing_page: canonicalUrl(locationLike && locationLike.href, origin),
      referrer: isExternalReferrer(referrer, origin) ? canonicalUrl(referrer) : "",
    };
    ALL_MARKETING_KEYS.forEach(function (key) {
      if (params[key]) touch[key] = params[key];
    });
    return touch;
  }

  function normalizeTouch(input) {
    var source = input && typeof input === "object" ? input : {};
    var touch = {};
    var capturedAt = clean(source.captured_at, 40);
    var landingPage = canonicalUrl(source.landing_page);
    var referrer = canonicalUrl(source.referrer);
    if (capturedAt) touch.captured_at = capturedAt;
    if (landingPage) touch.landing_page = landingPage;
    if (referrer) touch.referrer = referrer;
    ALL_MARKETING_KEYS.forEach(function (key) {
      var value = clean(source[key], key.indexOf("utm_") === 0 ? 300 : 256);
      if (value) touch[key] = value;
    });
    return touch;
  }

  // Requested booking context contains product choices only, never contact data.
  function normalizeStorefrontContext(input) {
    var src = input && typeof input === "object" ? input : {}, out = {};
    var checks = {
      product_handle: /^[a-z0-9][a-z0-9-]{0,119}$/,
      variant_id: /^[0-9]{1,24}$/,
      country: /^[A-Z]{2}$/,
      region: /^(au|uk|eu|nz)$/,
      demo_type: /^(virtual|quick_qa|post_sale|physical)$/,
      source_path: /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:products|pages)\/[a-z0-9-]{1,120}$/,
      entry_point: /^[a-z0-9_]{1,60}$/,
      assistance_reference: /^BM-[a-f0-9]{24}$/i
    };
    Object.keys(checks).forEach(function (key) { var value = clean(src[key], 160); if (checks[key].test(value)) out[key] = value; });
    return out;
  }

  function contextForBooking(windowLike, target) {
    var params = new URLSearchParams(windowLike.location.search || "");
    var context = {};
    ["product_handle", "variant_id", "country", "region", "source_path", "entry_point", "assistance_reference"].forEach(function (key) {
      var value = params.get("bm_" + key); if (value) context[key] = value;
    });
    var product = String(windowLike.location.pathname || "").match(/\/products\/([a-z0-9-]+)\/?$/);
    if (product) {
      context.product_handle = product[1]; context.source_path = windowLike.location.pathname.replace(/\/$/, "");
      var variant = params.get("variant"); if (variant) context.variant_id = variant;
      context.entry_point = "product_page";
    }
    // The live configurator knows canonical handles and a selection without a URL variant.
    try {
      var sales = windowLike.BMSalesExperience;
      if (sales && typeof sales.context === "function") {
        var selected = sales.context() || {};
        var productChoice = normalizeStorefrontContext({product_handle:selected.product_handle,variant_id:selected.variant_id});
        Object.keys(productChoice).forEach(function (key) { context[key] = productChoice[key]; });
      }
    } catch (_error) {}
    if (windowLike.Shopify && windowLike.Shopify.country) context.country = windowLike.Shopify.country;
    var hostname = windowLike.location.hostname;
    context.region = /(?:^|\.)speediance\.com\.au$/.test(hostname) ? "au" : /(?:^|\.)speediance\.nz$/.test(hostname) ? "nz" : /(?:^|\.)speediance\.co\.uk$/.test(hostname) ? "uk" : "eu";
    if (!context.source_path) context.source_path = windowLike.location.pathname.replace(/\/$/, "");
    if (target && typeof target.getAttribute === "function") {
      ["country", "region", "demo_type", "entry_point", "assistance_reference"].forEach(function (key) {
        var value = target.getAttribute("data-bm-" + key.replace(/_/g, "-"));
        if (value != null) context[key] = value;
      });
    }
    return normalizeStorefrontContext(context);
  }

  function augmentServiceContext(value, context, storeHost, base) {
    var parsed; try { parsed = new URL(value, base); } catch (_error) { return value; }
    if (parsed.hostname.toLowerCase() !== CAL_HOST) return value;
    var cx = normalizeStorefrontContext(context);
    if (!Object.keys(cx).length) return value;
    ALL_MARKETING_KEYS.forEach(function (key) { parsed.searchParams.delete(key); });
    // No marketing touch or identity is included in this transient service request.
    parsed.searchParams.set("utm_content", PACKED_PREFIX + utf8ToBase64Url(JSON.stringify({v:VERSION,sh:clean(storeHost,255),cx:cx})));
    return parsed.toString();
  }

  function normalizeState(input) {
    if (!input || typeof input !== "object") return null;
    var first = normalizeTouch(input.first_touch);
    var last = normalizeTouch(input.last_touch || input.first_touch);
    if (!first.landing_page && !last.landing_page) return null;
    return {
      schema_version: VERSION,
      store_host: clean(input.store_host, 255),
      storefront_context: normalizeStorefrontContext(input.storefront_context),
      first_touch: first,
      last_touch: last,
    };
  }

  function updateState(previous, currentTouch, storeHost) {
    var existing = normalizeState(previous);
    var current = normalizeTouch(currentTouch);
    if (!existing) {
      return normalizeState({
        schema_version: VERSION,
        store_host: storeHost,
        first_touch: current,
        last_touch: current,
      });
    }

    var hasNewSignal = hasMarketingSignal(current);
    var hasNewReferrer = Boolean(current.referrer);
    return normalizeState({
      schema_version: VERSION,
      store_host: clean(storeHost, 255) || existing.store_host,
      first_touch: existing.first_touch,
      last_touch: hasNewSignal || hasNewReferrer ? current : existing.last_touch,
    });
  }

  function compactTouch(touch) {
    var map = {
      captured_at: "ts", landing_page: "lp", referrer: "rf",
      gclid: "g", gbraid: "gb", wbraid: "wb",
      utm_source: "us", utm_medium: "um", utm_campaign: "uc",
      utm_term: "ut", utm_content: "uo",
      tmsrc: "ms", tmcid: "mc", tmsid: "md", tmid: "mi",
    };
    var output = {};
    Object.keys(map).forEach(function (key) {
      if (touch && touch[key]) output[map[key]] = touch[key];
    });
    return output;
  }

  function expandTouch(touch) {
    var map = {
      ts: "captured_at", lp: "landing_page", rf: "referrer",
      g: "gclid", gb: "gbraid", wb: "wbraid",
      us: "utm_source", um: "utm_medium", uc: "utm_campaign",
      ut: "utm_term", uo: "utm_content",
      ms: "tmsrc", mc: "tmcid", md: "tmsid", mi: "tmid",
    };
    var output = {};
    Object.keys(map).forEach(function (key) {
      if (touch && touch[key]) output[map[key]] = touch[key];
    });
    return normalizeTouch(output);
  }

  function utf8ToBase64Url(value) {
    var bytes = unescape(encodeURIComponent(value));
    var encoded;
    if (typeof btoa === "function") {
      encoded = btoa(bytes);
    } else if (typeof Buffer !== "undefined") {
      encoded = Buffer.from(value, "utf8").toString("base64");
    } else {
      throw new Error("No base64 encoder available");
    }
    return encoded.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
  }

  function base64UrlToUtf8(value) {
    var padded = String(value || "").replace(/-/g, "+").replace(/_/g, "/");
    while (padded.length % 4) padded += "=";
    if (typeof atob === "function") return decodeURIComponent(escape(atob(padded)));
    if (typeof Buffer !== "undefined") return Buffer.from(padded, "base64").toString("utf8");
    throw new Error("No base64 decoder available");
  }

  function packState(state) {
    var normalized = normalizeState(state);
    if (!normalized) return "";
    return PACKED_PREFIX + utf8ToBase64Url(JSON.stringify({
      v: VERSION,
      sh: normalized.store_host,
      f: compactTouch(normalized.first_touch),
      l: compactTouch(normalized.last_touch),
      cx: normalizeStorefrontContext(normalized.storefront_context),
    }));
  }

  function unpackState(value) {
    var packed = clean(value, 7000);
    if (packed.indexOf(PACKED_PREFIX) !== 0) return null;
    try {
      var decoded = JSON.parse(base64UrlToUtf8(packed.slice(PACKED_PREFIX.length)));
      if (decoded.v !== VERSION) return null;
      return normalizeState({
        schema_version: decoded.v,
        store_host: decoded.sh,
        first_touch: expandTouch(decoded.f),
        last_touch: expandTouch(decoded.l),
        storefront_context: decoded.cx,
      });
    } catch (_error) {
      return null;
    }
  }

  function augmentCalUrl(value, state, base) {
    var parsed;
    try { parsed = new URL(value, base || undefined); } catch (_error) { return value; }
    if (parsed.hostname.toLowerCase() !== CAL_HOST) return value;
    var normalized = normalizeState(state);
    if (!normalized) return parsed.toString();

    var last = normalized.last_touch || {};
    var hasGoogleClick = Boolean(last.gclid || last.gbraid || last.wbraid);
    var source = last.utm_source || (hasGoogleClick ? "google" : last.tmsrc || "");
    var medium = last.utm_medium || (hasGoogleClick ? "cpc" : "");
    var standard = {
      utm_source: source,
      utm_medium: medium,
      utm_campaign: last.utm_campaign || "",
      utm_term: last.utm_term || "",
    };
    Object.keys(standard).forEach(function (key) {
      if (standard[key]) parsed.searchParams.set(key, standard[key]);
    });
    parsed.searchParams.set("utm_content", packState(normalized));
    return parsed.toString();
  }

  function readCookie(documentLike) {
    var cookies = String((documentLike && documentLike.cookie) || "").split(/;\s*/);
    for (var index = 0; index < cookies.length; index += 1) {
      var separator = cookies[index].indexOf("=");
      var name = separator >= 0 ? cookies[index].slice(0, separator) : cookies[index];
      if (name !== COOKIE_NAME) continue;
      try { return unpackState(decodeURIComponent(cookies[index].slice(separator + 1))); } catch (_error) { return null; }
    }
    return null;
  }

  function readStoredState(windowLike) {
    var fromCookie = readCookie(windowLike.document);
    if (fromCookie) return fromCookie;
    try { return unpackState(windowLike.localStorage.getItem(STORAGE_KEY)); } catch (_error) { return null; }
  }

  function analyticsStorageAllowed(windowLike) {
    try {
      var privacy = windowLike.Shopify && windowLike.Shopify.customerPrivacy;
      // Fail closed: never persist or hand attribution to Cal until Shopify's
      // consent API explicitly says analytics processing is allowed.
      if (!privacy || typeof privacy.analyticsProcessingAllowed !== "function") return false;
      return privacy.analyticsProcessingAllowed() === true;
    } catch (_error) {
      return false;
    }
  }

  function persistState(windowLike, state) {
    if (!analyticsStorageAllowed(windowLike)) return false;
    var packed = packState(state);
    if (!packed) return false;
    try { windowLike.localStorage.setItem(STORAGE_KEY, packed); } catch (_error) {}
    try {
      windowLike.document.cookie = COOKIE_NAME + "=" + encodeURIComponent(packed)
        + ";path=/;max-age=" + COOKIE_MAX_AGE + ";SameSite=Lax;Secure";
    } catch (_error) {}
    return true;
  }

  function findFormDestination(form) {
    if (!form || typeof form.closest !== "function") return "";
    var host = form.closest("book-a-demo-v2, book-a-demo, [data-section-id]");
    if (!host) return "";
    var dataScript = host.querySelector('script[type="application/json"][id^="locations-"]');
    var demoType = host.querySelector('select[id^="demoType-"]');
    var country = host.querySelector('select[id^="country-"]');
    if (!dataScript || !demoType || !country || !demoType.value || !country.value) return "";
    var locations;
    try { locations = (JSON.parse(dataScript.textContent || "{}").locations || []); } catch (_error) { return ""; }
    var handle = demoType.value === "physical"
      ? ((host.querySelector('input[name="location"]:checked') || {}).value || "")
      : country.value;
    var location = locations.filter(function (item) { return item.country_handle === handle; })[0];
    if (!location) return "";
    var keyByType = {
      physical: "url_physical",
      virtual: "url_virtual",
      quick_qa: "url_quick_qa",
      post_sale: "url_post_sale",
    };
    return clean(location[keyByType[demoType.value]], 2000);
  }

  function install(windowLike) {
    if (windowLike.__bmDemoAttributionInstalled) return;
    windowLike.__bmDemoAttributionInstalled = true;
    var currentState = null;
    var consentGranted = false;

    function enableAfterConsent() {
      if (!analyticsStorageAllowed(windowLike)) return false;
      consentGranted = true;
      currentState = updateState(
        readStoredState(windowLike),
        buildTouch(windowLike.location, windowLike.document.referrer, new Date().toISOString()),
        windowLike.location.hostname
      );
      persistState(windowLike, currentState);
      return true;
    }

    function decorateBookingUrl(destination, target) {
      var context = contextForBooking(windowLike, target);
      if (consentGranted && currentState && analyticsStorageAllowed(windowLike)) {
        var bookingState = Object.assign({}, currentState, {storefront_context:context});
        return augmentCalUrl(destination, bookingState, windowLike.location.href);
      }
      return augmentServiceContext(destination, context, windowLike.location.hostname, windowLike.location.href);
    }
    windowLike.BMDemoAttribution = windowLike.BMDemoAttribution || {};
    windowLike.BMDemoAttribution.decorateBookingUrl = decorateBookingUrl;

    enableAfterConsent();

    windowLike.document.addEventListener("visitorConsentCollected", function () {
      enableAfterConsent();
    });

    windowLike.document.addEventListener("click", function (event) {
      var target = event.target && typeof event.target.closest === "function"
        ? event.target.closest("a[href], button[data-online][data-url]")
        : null;
      if (!target) return;
      var isButton = target.tagName && target.tagName.toLowerCase() === "button";
      var destination = isButton ? target.getAttribute("data-url") : target.href;
      var parsed; try { parsed = new URL(destination, windowLike.location.href); } catch (_error) { return; }
      var context = contextForBooking(windowLike, target);
      // Carry explicit selections to the demo page in the link only. No storage is needed.
      if (parsed.origin === windowLike.location.origin && /\/pages\/book-a-demo\/?$/.test(parsed.pathname) && context.product_handle) {
        Object.keys(context).forEach(function (key) { parsed.searchParams.set("bm_" + key, context[key]); });
        target.href = parsed.toString(); return;
      }
      if (parsed.hostname.toLowerCase() !== CAL_HOST) return;
      var augmented = decorateBookingUrl(destination, target);
      if (isButton) target.setAttribute("data-url", augmented); else target.href = augmented;
    }, true);

    windowLike.document.addEventListener("submit", function (event) {
      if (!consentGranted || !currentState || !analyticsStorageAllowed(windowLike)) return;
      var destination = findFormDestination(event.target);
      if (!destination) return;
      var augmented = augmentCalUrl(destination, currentState, windowLike.location.href);
      if (augmented === destination) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      windowLike.location.assign(augmented);
    }, true);
  }

  return {
    VERSION: VERSION,
    COOKIE_NAME: COOKIE_NAME,
    STORAGE_KEY: STORAGE_KEY,
    PACKED_PREFIX: PACKED_PREFIX,
    marketingParams: marketingParams,
    buildTouch: buildTouch,
    updateState: updateState,
    normalizeState: normalizeState,
    normalizeStorefrontContext: normalizeStorefrontContext,
    contextForBooking: contextForBooking,
    augmentServiceContext: augmentServiceContext,
    packState: packState,
    unpackState: unpackState,
    augmentCalUrl: augmentCalUrl,
    findFormDestination: findFormDestination,
    analyticsStorageAllowed: analyticsStorageAllowed,
    install: install,
  };
});
